import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { Calendar, Clock, HandCoins, MessageSquare, User, Users } from 'lucide-react'
import { BoxTruckLoadAnnotation, BoxTruckOperatorBid, LoadBid } from '@numeo/types'
import { CanonicalLoad } from '@numeo/types-core'
import { CallIcon, EmailIcon } from 'features/load-hub/icons'
import { useCallProvider } from 'features/load-hub/hooks/useCallProvider'
import { useExtensionInstalled } from 'hooks/useExtensionInstalled'
import { initiateLoadHubCall } from 'features/load-hub/utils/callProvider'
import { getFormattedPhone } from 'features/load-hub/utils/phone'
import { saveCurrentLoad } from 'features/load-hub/components/expanded-row/ContactSection'
import { BoxTruckLoadMapModal } from './BoxTruckLoadMapModal'
import { BoxTruckLoadRouteMiniMap } from './BoxTruckLoadRouteMiniMap'
import { BoxTruckRouteTeaser } from './BoxTruckRouteTeaser'
import { fmtDateTime, fmtExpires, fmtMiles } from '../utils/format'
import { bidderLabel, fmtBidPrice } from '../utils/bidFormat'
import { brokerEmail, brokerName, brokerPhone, deliveryLabel, localityWithZip, pickupLabel, stopLocalTimeTitle } from '../utils/loadFields'
import { BoxTruckFactoringBadge } from './BoxTruckFactoringBadge'
import { BoxTruckNoteFacts } from './BoxTruckNoteFacts'
import { trackBoxTruckUiInteraction } from '../utils/analytics'
import { LoadSpecifications } from './BoxTruckLoadSpecs'
import { StarRating } from './StarRating'
import { CopyButton } from './BoxTruckDrawerActions'

/**
 * Who bid on this load, what they asked, and the way to answer them.
 *
 * The bid names a driver, so the conversation with that driver is one press away (ADR-057): a dispatcher
 * reading a price has a question about it, and finding the same person in the SMS page means leaving the
 * load. `onText` is absent where there is no room for the pane (the mobile drawer), and the list then reads
 * exactly as it did.
 */
const DriverBids = ({ bids, onText }: { bids: LoadBid[]; onText?: (bid: LoadBid) => void }) => {
    if (bids.length === 0) return null
    return (
        <>
            <div className="mt-5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-foreground-muted">
                <Users size={12} /> Driver Bids <span className="text-success">({bids.length})</span>
            </div>
            <div className="mt-2 space-y-1.5">
                {bids.map((bid) => (
                    <div key={bid.bidderId} className="flex items-center justify-between gap-3 rounded-lg bg-background px-3 py-2">
                        <div className="min-w-0">
                            <div className="truncate text-sm font-medium text-foreground">{bidderLabel(bid)}</div>
                            {bid.createdAt && <div className="text-[11px] text-foreground-muted">{fmtDateTime(bid.createdAt)}</div>}
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                            <span className="text-sm font-semibold text-success">{fmtBidPrice(bid.price, bid.currency)}</span>
                            {onText && (
                                <button
                                    type="button"
                                    onClick={() => onText(bid)}
                                    title={`Open the SMS conversation with ${bidderLabel(bid)}`}
                                    className="rounded-md p-1 text-foreground-muted transition-colors hover:bg-default hover:text-foreground"
                                >
                                    <MessageSquare size={14} />
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </>
    )
}

const OperatorBidEmails = ({ bids, currentOperatorId }: { bids: BoxTruckOperatorBid[]; currentOperatorId?: string }) => {
    if (bids.length === 0) return null
    return (
        <>
            <div className="mt-5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-foreground-muted">
                <Users size={12} /> Operator bid emails <span className="text-success">({bids.length})</span>
            </div>
            <div className="mt-2 space-y-1.5">
                {bids.map((bid) => {
                    const isCurrentOperator = !!currentOperatorId && bid.operatorId === currentOperatorId
                    return (
                        <div
                            key={bid.operatorId}
                            className={`flex items-center justify-between gap-3 rounded-lg px-3 py-2 ${isCurrentOperator ? 'bg-accent-soft' : 'bg-success-soft'}`}
                        >
                            <div className="min-w-0">
                                <div
                                    className={`truncate text-sm font-medium ${isCurrentOperator ? 'text-accent-soft-foreground' : 'text-success-soft-foreground'}`}
                                    title={bid.operatorEmail ?? undefined}
                                >
                                    {/* A teammate is recognised by NAME — the email was all this card had, and a
                                        long address truncates to "jamesbei@2131truck…", which names nobody. The
                                        address stays as the title and as the fallback for an account with no
                                        name on it. */}
                                    {isCurrentOperator ? 'You' : bid.operatorName || bid.operatorEmail || 'Unknown operator'}
                                </div>
                                <div className="text-[11px] text-foreground-muted">{isCurrentOperator ? 'You sent this bid email' : 'Bid email sent'}</div>
                            </div>
                            <span className="shrink-0 text-[11px] text-foreground-muted">{fmtDateTime(bid.createdAt)}</span>
                        </div>
                    )
                })}
            </div>
        </>
    )
}

/** The team's shared star rating + note on the load. The rating saves immediately on a star click; the note
 *  has its own Save (so typing doesn't fire a write per keystroke). Each save sends the FULL state, so the
 *  two never clobber each other. Re-seeds when the load changes or a teammate's update lands over WS. */
const TeamAnnotation = ({
    load,
    annotation,
    onSet,
}: {
    load: CanonicalLoad
    annotation: BoxTruckLoadAnnotation | null
    onSet: (load: CanonicalLoad, rating: number | null, comment: string | null, kind: 'rating' | 'note') => void
}) => {
    const [comment, setComment] = useState(annotation?.comment ?? '')
    // Track the latest local value + the last-seeded saved value in refs so the reseed effect doesn't re-run
    // on every keystroke (and doesn't need `comment` as a dep).
    const commentRef = useRef(comment)
    commentRef.current = comment
    const loadRef = useRef(load.externalId)
    const savedRef = useRef(annotation?.comment ?? '')
    useEffect(() => {
        const incoming = annotation?.comment ?? ''
        // Reseed on a load switch (always), or when the saved note changed AND there are no unsaved local edits.
        // Otherwise leave the in-progress text alone so an overlay refresh carrying a teammate's newer note
        // can't silently clobber one this operator is still typing.
        if (loadRef.current !== load.externalId || commentRef.current === savedRef.current) setComment(incoming)
        loadRef.current = load.externalId
        savedRef.current = incoming
    }, [annotation?.comment, load.externalId])
    const rating = annotation?.rating ?? null
    const noteDirty = (comment.trim() || null) !== (annotation?.comment ?? null)
    return (
        <>
            <div className="mt-4 flex items-center justify-between gap-2">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-foreground-muted">Team rating &amp; note</span>
                <StarRating value={rating} size={17} onChange={(r) => onSet(load, r, annotation?.comment ?? null, 'rating')} />
            </div>
            <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add a note for your team…"
                rows={2}
                className="mt-2 w-full resize-y rounded-lg border border-border bg-background px-3 py-2 text-[13px] text-foreground placeholder:text-foreground-subtle focus:border-accent focus:outline-none"
            />
            <div className="mt-1.5 flex items-center justify-between gap-2">
                <span className="truncate text-[11px] text-foreground-subtle">
                    {annotation?.updatedByEmail ? `Last set by ${annotation.updatedByEmail}` : ''}
                </span>
                <button
                    type="button"
                    disabled={!noteDirty}
                    onClick={() => onSet(load, rating, comment.trim() || null, 'note')}
                    className={`shrink-0 rounded-md px-2.5 py-1 text-xs font-semibold transition-colors ${
                        noteDirty ? 'bg-accent text-accent-foreground hover:bg-accent-hover' : 'cursor-not-allowed bg-default text-foreground-subtle'
                    }`}
                >
                    Save note
                </button>
            </div>
        </>
    )
}

/**
 * The posted rate worth showing: a finite number ABOVE ZERO.
 *
 * Not simply "not null". The board unions Sylectus with DAT, and the two normalizers disagree about zero:
 * Sylectus runs `postedAmount` through `positiveOrNull`, so its bid-driven freight ("Posted Amount: $0.00")
 * arrives as null — but DAT's takes `rateInfo.rateUsd ?? null` and passes a zero straight through. A null
 * check alone therefore reads correctly on one source and prints "Target rate $0" on the other, which states
 * an offer of nothing where the truth is that no rate was posted.
 *
 * Coerced rather than compared directly: this column reaches the client as a pg `numeric`, which arrives as a
 * string — the same reason `loadMetrics` wraps every read of it in `Number()`.
 */
const postedRate = (load: CanonicalLoad): number | null => {
    if (load.rateAmountUsd == null) return null
    const value = Number(load.rateAmountUsd)
    return Number.isFinite(value) && value > 0 ? value : null
}

export const LoadDetails = ({
    load,
    bids,
    operatorBids,
    annotation,
    onSetAnnotation,
    currentOperatorId,
    afterPrimaryDetails,
    openMapOnExpand,
    onTextBidder,
}: {
    load: CanonicalLoad
    bids: LoadBid[]
    operatorBids: BoxTruckOperatorBid[]
    annotation: BoxTruckLoadAnnotation | null
    onSetAnnotation?: (load: CanonicalLoad, rating: number | null, comment: string | null, kind: 'rating' | 'note') => void
    currentOperatorId?: string
    /** Mobile drawer slot rendered after route/specification details and before secondary broker content. */
    afterPrimaryDetails?: ReactNode
    /** The operator's "Open map when a load is opened" preference — see BoxTruckBoardPreferences. */
    openMapOnExpand?: boolean
    /** Open the SMS conversation with a driver who bid. Absent → the bid list shows no such button. */
    onTextBidder?: (bid: LoadBid) => void
}) => {
    const [mapOpen, setMapOpen] = useState(false)
    const brokerPostedRate = postedRate(load)
    const email = brokerEmail(load)
    const contactName = brokerName(load)
    const phoneInfo = getFormattedPhone(brokerPhone(load))
    const isDat = load.sourceType === 'DAT' || load.sourceType === 'DAT_EXTENSION'
    const { provider: callProvider } = useCallProvider()
    const { confirmed: extensionConfirmed } = useExtensionInstalled()

    // Phone action mirrors Load Hub: save the load for the extension, and when the extension isn't
    // confirmed installed, dial natively via the selected call provider (the .rc_call_icon class lets
    // the extension's content script own the click when it IS installed, avoiding a double-call).
    const handleCall = useCallback(() => {
        saveCurrentLoad(load, 'call')
        if (!extensionConfirmed && phoneInfo?.formatted) initiateLoadHubCall(callProvider, phoneInfo.formatted)
    }, [load, extensionConfirmed, callProvider, phoneInfo])

    // Opening the planner is now the ONLY way a route gets computed for this load, so it is also the only
    // honest measure of how often the map is actually wanted per load opened.
    const handleExpandMap = useCallback(() => {
        trackBoxTruckUiInteraction('map_opened')
        setMapOpen(true)
    }, [])

    return (
        <div className="flex flex-col">
            {/* Route & schedule */}
            <div className="order-1 rounded-xl border border-border bg-background p-3.5">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-success">
                            <span className="h-2 w-2 rounded-full bg-success" /> Pickup
                        </div>
                        <div className="mt-1 text-sm font-semibold text-foreground">
                            {localityWithZip(load.pickupCity, load.pickupStateProv, load.pickupPostalCode)}
                        </div>
                        <div
                            className="mt-1 flex items-center gap-1 text-xs text-foreground-muted"
                            title={stopLocalTimeTitle(load.details?.sylectusPickupText, load.pickupWindowStart)}
                        >
                            <Calendar size={12} /> {pickupLabel(load)}
                        </div>
                    </div>
                    <div className="flex shrink-0 flex-col items-center pt-4 text-[11px] font-medium text-foreground-muted">
                        <span className="text-base leading-none">→</span>
                        <span className="mt-1">{fmtMiles(load.distanceMiles)}</span>
                    </div>
                    <div className="min-w-0 flex-1 text-right">
                        <div className="flex items-center justify-end gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-danger">
                            Delivery <span className="h-2 w-2 rounded-full bg-danger" />
                        </div>
                        <div className="mt-1 text-sm font-semibold text-foreground">
                            {localityWithZip(load.deliveryCity, load.deliveryStateProv, load.deliveryPostalCode)}
                        </div>
                        <div
                            className="mt-1 flex items-center justify-end gap-1 text-xs text-foreground-muted"
                            title={stopLocalTimeTitle(load.details?.sylectusDeliveryText, load.deliveryWindowStart)}
                        >
                            <Calendar size={12} /> {deliveryLabel(load)}
                        </div>
                    </div>
                </div>
                {load.details?.sylectusExpiresAt && (
                    <div className="mt-3 flex items-center gap-2 border-t border-border pt-2.5">
                        <Clock size={14} className="text-warning" />
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-foreground-muted">Expires</span>
                        <span className="ml-auto text-sm font-semibold text-foreground">{fmtExpires(load.details.sylectusExpiresAt)}</span>
                    </div>
                )}
            </div>

            {/* Broker follows notes and stays immediately above the map. Mobile also keeps it below the injected
                Nearby Drivers/email workspace. Email opens the mail client; phone uses the Load Hub call path. */}
            <div className="order-5 mt-4 text-[10px] font-semibold uppercase tracking-wider text-foreground-muted min-[900px]:order-4">Broker</div>
            <div className="order-5 mt-2 rounded-lg bg-background px-4 py-3 min-[900px]:order-4">
                <div className="text-sm font-semibold text-foreground">{load.brokerCompanyName || '—'}</div>
                {/* The rate the broker posted — the number that decides whether this load is worth bidding on.
                    It already seeds Price for Broker in the bid calculator, and that was the ONLY place it
                    appeared: a dispatcher scanning the drawer had to open the bid panel to learn what the load
                    pays. Same field, read where the broker is read.

                    Shown only for a real number — see {@link postedRate} for why "not null" is not enough. */}
                {brokerPostedRate != null && (
                    <div className="mt-1.5 flex items-center gap-1.5">
                        <HandCoins size={15} className="shrink-0 text-success" />
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-foreground-muted">Target rate</span>
                        <span className="ml-auto text-sm font-bold text-success">{fmtBidPrice(brokerPostedRate, load.rateCurrency)}</span>
                    </div>
                )}
                {/* The person, under the brokerage that employs them — the order the Sylectus email states them
                    in, and what an operator reads out when the phone is answered. Omitted entirely when the
                    source carried no name (DAT, the Sylectus API sweep) rather than holding an empty row.
                 */}
                {contactName && (
                    <div className="mt-1 flex items-center gap-1.5">
                        <div className="flex min-w-0 items-center gap-1.5 text-sm font-medium text-foreground-muted">
                            <User size={15} className="shrink-0" />
                            <span className="truncate" title={contactName}>
                                {contactName}
                            </span>
                        </div>
                        <CopyButton value={contactName} label="Copy name" />
                    </div>
                )}
                {email && (
                    <div className="mt-1.5 flex items-center gap-1.5">
                        <a
                            href={`mailto:${email}`}
                            title="Email broker"
                            className="flex min-w-0 items-center gap-1.5 text-sm font-medium text-accent hover:underline"
                        >
                            <EmailIcon sx={{ width: 15, height: 15, color: 'inherit' }} /> <span className="truncate">{email}</span>
                        </a>
                        <CopyButton value={email} label="Copy email" />
                    </div>
                )}
                {phoneInfo?.formatted && (
                    <div className="mt-1 flex items-center gap-1.5">
                        <button
                            type="button"
                            id={`${load.id}`}
                            onClick={handleCall}
                            title="Call broker"
                            className="rc_call_icon flex min-w-0 items-center gap-1.5 border-none bg-transparent p-0 text-sm font-medium text-foreground-muted hover:text-accent"
                        >
                            <CallIcon sx={{ width: 15, height: 15, color: 'inherit' }} /> <span className="truncate">{phoneInfo.formatted}</span>
                        </button>
                        <CopyButton value={phoneInfo.formatted} label="Copy phone" />
                    </div>
                )}
                {/* RTS/TAFS factoring rating — by MC if the load has one, else by broker company name. */}
                <BoxTruckFactoringBadge load={load} />
                {/* Broker credit — a DAT concept (credit score + days-to-pay). Shown for every DAT load with a
                    stable "—" when a value is absent, rather than the row vanishing; hidden for non-DAT sources
                    (Sylectus etc.) which have no credit concept at all. */}
                {isDat && (
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-border pt-2 text-xs">
                        <span className="text-foreground-muted">
                            Credit score <span className="font-semibold text-foreground">{load.details?.credit_score ?? '—'}</span>
                        </span>
                        <span className="text-foreground-muted">
                            Days to pay <span className="font-semibold text-foreground">{load.details?.days_to_pay ?? '—'}</span>
                        </span>
                    </div>
                )}
            </div>

            {/* Load notes — shown after specifications and before broker details. */}
            {load.comment && (
                <>
                    <div className="order-4 mt-4 text-[10px] font-semibold uppercase tracking-wider text-foreground-muted min-[900px]:order-3">Notes</div>
                    <div className="order-4 mt-2 whitespace-pre-wrap rounded-lg bg-background px-3 py-2 text-[13px] font-medium text-foreground min-[900px]:order-3">
                        {load.comment}
                    </div>
                </>
            )}

            {/* The same note, read into fields (ADR-049). Under the note and not instead of it: the reading is
                an inference, and the text above is what the broker actually wrote. Renders nothing when no
                model has read it, which is most loads until the extractor catches up. */}
            <BoxTruckNoteFacts load={load} />

            {/* Origin → destination route — a drawn teaser, not a live map. The Load Hub map that used to sit
                here cost an OSRM route, up to three geocodes and thirty-odd OSM tiles on EVERY drawer open,
                and the board is scanned by opening loads in quick succession. The real route now comes from
                the Google-backed planner below, which only mounts on the click. */}
            <div className="order-last mt-4">
                {openMapOnExpand ? (
                    <BoxTruckLoadRouteMiniMap load={load} onExpand={handleExpandMap} />
                ) : (
                    <BoxTruckRouteTeaser load={load} onExpand={handleExpandMap} />
                )}
            </div>
            {mapOpen && <BoxTruckLoadMapModal load={load} onClose={() => setMapOpen(false)} />}

            {/* Driver bids — the live carrier offers on this load (best price first) */}
            <div className="order-2 min-[900px]:order-6">
                <DriverBids bids={bids} onText={onTextBidder} />
                <OperatorBidEmails bids={operatorBids} currentOperatorId={currentOperatorId} />
            </div>

            {/* Load specifications — source-aware: Sylectus keeps its requirement chips, other sources show
                their canonical loadboard fields (Reference ID, commodity, length, requirements). */}
            <div className="order-2">
                <LoadSpecifications load={load} />
            </div>

            {afterPrimaryDetails && <div className="order-3">{afterPrimaryDetails}</div>}

            {/* Team rating + shared note — follows the load facts, before the final route map. */}
            {onSetAnnotation && (
                <div className="order-7">
                    <TeamAnnotation load={load} annotation={annotation} onSet={onSetAnnotation} />
                </div>
            )}
        </div>
    )
}
